import React, { useEffect, useMemo, useState } from 'react';
import { CollapseBlock, Spinner } from '../../../../../common/index.jsx';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import { AppBarCMS } from '../../../../../basic/index.jsx';
import EduCoursesTable from '../../../../../common/FriEdu/EduCoursesTable.jsx';
import { useTranslation } from 'react-i18next';
import {
  useDeleteEduCourseMutation,
  useGetEduCoursesQuery,
} from '../../../../../../redux/services/eduCourses.jsx';
import { useNavigate } from 'react-router-dom';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import TableFilter from '../../../../../common/TableFilter/TableFilter.jsx';
import { useSelector } from 'react-redux';
import { isUserAndItsMorphIdentical } from '../../../../../../utils/helpers/CommonHelper.jsx';
import useUserHasRole from '../../../../../../hooks/useUserHasRoles.jsx';
import { ROLE_FRI_OPEN_LECTURER } from '../../../../../../utils/constants/roles.js';

const TABLE_FILTER_TITLE = 'Filter kurzov';

export const EduCoursesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const isFriOpenLecturer = useUserHasRole(ROLE_FRI_OPEN_LECTURER);

  const { data: courses, isLoading, refetch } = useGetEduCoursesQuery();
  const [deleteEduCourse, { isSuccess: isDeleteSuccess }] =
    useDeleteEduCourseMutation();

  const { deleteSuccess: toastDeleteSuccess } = useToastMessages();

  // remember expanded rows across navigation
  const [expandedRowKeys, setExpandedRowKeys] = useState(() => {
    try {
      const saved = localStorage.getItem('edu-courses-table-expanded-rows');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // filter panel state (persisted by TableFilter using title key)
  const [selectedListOption, setSelectedListOption] = useState(
    localStorage.getItem(TABLE_FILTER_TITLE) || 'active',
  );

  // Coerce legacy/persisted value 'mine_participant' back to 'active'
  useEffect(() => {
    if (selectedListOption === 'mine_participant') {
      setSelectedListOption('active');
      try {
        localStorage.setItem(TABLE_FILTER_TITLE, 'active');
      } catch {}
    }
  }, [selectedListOption]);

  // If user is not FRI.open lecturer, force 'active' view and hide filter panel
  useEffect(() => {
    if (!isFriOpenLecturer && selectedListOption !== 'active') {
      setSelectedListOption('active');
      try {
        localStorage.setItem(TABLE_FILTER_TITLE, 'active');
      } catch {}
    }
  }, [isFriOpenLecturer]);

  useEffect(() => {
    if (isDeleteSuccess) {
      toastDeleteSuccess();
      refetch();
    }
  }, [isDeleteSuccess]);

  // Set CMS page title
  useEffect(() => {
    document.title = 'FRI.edu - ' + t('Zoznam kurzov');
  }, [t]);

  const handleExpandedRowsChange = (newExpandedRowKeys) => {
    setExpandedRowKeys(newExpandedRowKeys);
    localStorage.setItem(
      'edu-courses-table-expanded-rows',
      JSON.stringify(newExpandedRowKeys),
    );
  };

  // normalize raw list for filtering
  const allCourses = useMemo(() => {
    return Array.isArray(courses)
      ? courses
      : Array.isArray(courses?.data)
        ? courses.data
        : [];
  }, [courses]);

  // apply filter (force active when no lecturer role)
  const filteredCourses = useMemo(() => {
    if (!isFriOpenLecturer) {
      return allCourses.filter((c) => !c?.archived_at);
    }
    if (selectedListOption === 'mine_lecturer') {
      return allCourses.filter(
        (c) =>
          !c?.archived_at && isUserAndItsMorphIdentical(user, c?.lecturerable),
      );
    }
    if (selectedListOption === 'archived') {
      return allCourses.filter((c) => !!c?.archived_at);
    }
    // active (default)
    return allCourses.filter((c) => !c?.archived_at);
  }, [allCourses, selectedListOption, user, isFriOpenLecturer]);

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={t('Zoznam kurzov')}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        createText={t('Pridať kurz')}
        createUrl={routes.cmsCoursesCreate}
        permissionsBasic="fri-edu-courses"
      />
      <div style={{ margin: '10px' }}>
        {isFriOpenLecturer && (
          <TableFilter
            title={t(TABLE_FILTER_TITLE)}
            value={selectedListOption}
            setValue={setSelectedListOption}
            options={[
              { value: 'mine_lecturer', label: t('Moje aktívne kurzy') },
              { value: 'active', label: t('Všetky aktívne kurzy') },
              { value: 'archived', label: t('Archivované kurzy') },
            ]}
          />
        )}

        <CollapseBlock title={t('Kurzy FRI.edu')}>
          <EduCoursesTable
            courses={filteredCourses}
            onShowDatesClick={(course) =>
              navigate(`${routes.cmsEduDatesCreate}/${course.id}`)
            }
            onDelete={(record) => deleteEduCourse(record.id)}
            onEditClick={(record) =>
              navigate(`${routes.cmsCoursesEdit}/${record.id}`)
            }
            onDetailClick={(record) =>
              navigate(`${routes.cmsCourseDetail}/${record.id}`)
            }
            onRowClick={(record) =>
              navigate(`${routes.cmsCoursesEdit}/${record.id}`)
            }
            expandedRowKeys={expandedRowKeys}
            onExpandedRowsChange={handleExpandedRowsChange}
            onRefetchCourses={refetch}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default EduCoursesPage;
